import Flex from '@Components/Bases/Flex';
import ConfirmModal from '@Components/Modal/ConfirmModal';
import OtherExplanations from '@Containers/OtherExplanations';
import {
  INatureOfBusiness,
  useGetNatureOfBusiness,
  useGetNatureOfBusinessOneReport,
} from '@Domains/NatureOfBusiness';
import { useDisclosure } from '@hooks/useDisclosure';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { convertBuddhistYearToGregorian } from '@Utils/datetime';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import GeneralInformation from './Containers/GeneralInfomation';
import IncomeStructure from './Containers/IncomeStructure';
import MarketPolicies from './Containers/MarketPolicies';
import NatureOfBusinessCompletedSection from './Containers/NatureOfBusinessCompletedSection/NatureOfBusinessCompletedSection';
import NatureOfBusinessFooter from './Containers/NatureOfBusinessFooter/NatureOfBusinessFooter';
import NatureOfBusinessFormProvider from './Containers/NatureOfBusinessFormProvider';
import { INatureOfBusinessFormValues } from './Containers/NatureOfBusinessFormProvider/types';
import NatureOfBusinessOverview from './Containers/NatureOfBusinessOverview/NatureOfBusinessOverview';
import ProductInformation from './Containers/ProductInformation';
import { mapNatureOfBusinessToFormValues } from './utils';

const NatureOfBusiness = () => {
  const { id = '' } = useParams();
  const { user } = useProfile();
  const { progressData } = useProgressBar();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();

  const { refetchProgressWithUpdate, isPublished } = useProgressBar();
  const [openedModal, { open: openModal, close: closeModal }] = useDisclosure();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();

  const [oneReportData, setOneReportData] = useState<
    INatureOfBusiness | undefined
  >(undefined);

  const iCompany = user?.I_COMPANY;

  const startOperatingYear = convertBuddhistYearToGregorian(
    progressData?.report.startOperatingYear
  );

  const previousStartOperatingYear = startOperatingYear - 1;

  const [{ data, loading, error }, refetch] = useGetNatureOfBusiness(
    Number(id)
  );

  const [
    { loading: loadingOneReport, error: errorOneReport },
    executeOneReport,
  ] = useGetNatureOfBusinessOneReport(
    id,
    Number(iCompany),
    previousStartOperatingYear
  );

  useEffect(() => {
    if (currentPath === 'nature-of-business') {
      refetch().catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error('Failed to fetch nature of business:', err);
        }
      });
    }
  }, [currentPath, refetch]);

  const fetchNatureOfBusinessOneReport = useCallback(async () => {
    try {
      const response = await executeOneReport();

      if (response.data) {
        setOneReportData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch nature of business one report:', error);
      setOneReportData(undefined);
    } finally {
      openSnackbar();
      closeModal();
      refetchProgressWithUpdate(true);
      refetch();
    }
  }, [executeOneReport, refetch, openSnackbar, closeModal]);

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลลักษณะการประกอบธุรกิจได้
        </Alert>
      </Box>
    );
  }

  const onSubmit = () => (formValues: INatureOfBusinessFormValues) => {
    console.log('Form Values:', formValues);
  };

  const initialValues = data?.corporateInformation
    ? mapNatureOfBusinessToFormValues(oneReportData || data, user)
    : undefined;

  return (
    <>
      <NatureOfBusinessFormProvider
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        <Flex flexDirection={'column'} gap={2} p={2}>
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h3">ส่วนที่ 1 การประกอบธุรกิจ</Typography>
            {!isPublished && (
              <Button
                color={'inherit'}
                startIcon={<SyncIcon />}
                onClick={openModal}
              >
                ดึงข้อมูลจาก One Report
              </Button>
            )}
          </Flex>
          <NatureOfBusinessOverview />
          <GeneralInformation />
          <ProductInformation />
          <MarketPolicies />
          <IncomeStructure />
          <OtherExplanations />
          <NatureOfBusinessCompletedSection />
        </Flex>
        <NatureOfBusinessFooter />
      </NatureOfBusinessFormProvider>

      <ConfirmModal
        topic="ดึงข้อมูลล่าสุด"
        description="เมื่อกดยืนยันระบบจะดึงข้อมูลทุกรายการจากระบบ One Report และจะทำการแทนที่ข้อมูลที่ได้กรอกไว้ก่อนหน้านี้"
        opened={openedModal}
        close={closeModal}
        loading={loadingOneReport}
        confirm={fetchNatureOfBusinessOneReport}
        disableBackdropClose
      />

      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error || !!errorOneReport ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {errorOneReport?.status === 404
            ? 'ไม่พบข้อมูลจาก One Report'
            : !!error || !!errorOneReport
            ? 'เกิดข้อผิดพลาดในการดึงข้อมูล'
            : 'ดึงข้อมูลสำเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NatureOfBusiness;
